import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "convex/react";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { ChatInput } from "@/components/chat/ChatInput";
import type { ChatStarterSet } from "@/components/chat/chatStarters";
import { MessageList } from "@/components/chat/MessageList";
import { PodStatusBanner } from "@/components/chat/PodStatusBanner";
import { useChat } from "@/hooks/useChat";
import { useCreditBalance } from "@/hooks/useCreditBalance";
import { usePendingAttachments } from "@/hooks/usePendingAttachments";
import { usePodStatus } from "@/hooks/usePodStatus";
import { cn } from "@/lib/utils";

interface ChatWindowProps {
  threadId: Id<"threads">;
  authToken: string | null;
  className?: string;
  onWsStateChange?: (state: import("@/lib/wsManager").WsState) => void;
  /**
   * One-shot composer prefill (e.g. the workflows library "Describe a new
   * job" seed). Fills the input ONCE for the user to review + send manually —
   * never auto-sent. Reuses the existing starter-template `draft` seam.
   */
  initialDraft?: string;
  /**
   * Empty-thread starter bubbles for this surface (strategy injection —
   * `components/chat/chatStarters.ts`). Omit for the general chat set;
   * the workflows gestation route passes `WORKFLOW_CHAT_STARTERS`.
   */
  /** Injected strategy; `null` suppresses the starter UI entirely. */
  starterSet?: ChatStarterSet | null;
  /**
   * Fires ONCE per assistant turn — on each `isStreaming` true→false
   * transition (studio spec §3.4 turn-end signal). Strictly additive: no
   * behavior change when absent. Read through a ref so a new callback
   * identity never re-fires or suppresses a pending transition.
   */
  onAssistantTurnEnd?: () => void;
  /**
   * The first-run ritual is in flight and the agent has not spoken yet
   * (frontend-doctrine §4.16). Soft-locks the composer until the kickoff reply
   * lands, so the user does not type over the opener they are about to get.
   * The chat route passes `mode === "onboarding"`; surfaces whose threads the
   * kickoff deliberately skips (workflows / studio gestation) MUST NOT pass it,
   * or they would wait for an opener that is never coming.
   */
  awaitingKickoff?: boolean;
  /**
   * Promote an agent-written note to `/notebook?path=…` (§4.17). Forwarded
   * verbatim to `MessageList`; omit and the in-chat note cards still render
   * their preview, just without an Open affordance.
   */
  onOpenNote?: (path: string) => void;
}

/**
 * Failsafe for the composer soft-lock. The kickoff crosses Convex, a possibly
 * cold GKE pod and an LLM turn, so a slow one is normal and a lost one is
 * possible. This is NEVER a hard block: once it fires the user can type, and
 * their message is what the pod answers instead.
 */
const KICKOFF_WAIT_TIMEOUT_MS = 45_000;

/**
 * Detachable chat container — Invariant I3.
 * ZERO position/width/height/z-index styles.
 * Parent controls layout via className prop.
 */
export function ChatWindow({ threadId, authToken, className, onWsStateChange, initialDraft, starterSet, onAssistantTurnEnd, awaitingKickoff, onOpenNote }: ChatWindowProps) {
  const { isProvisioning, isError: isPodError, podState, podStateDetail } = usePodStatus();
  const credit = useCreditBalance();
  const attachments = usePendingAttachments();
  const {
    canLoadMore,
    isAgentActive,
    isLoading,
    isLoadingMore,
    isStreaming,
    isWaitingForResponse,
    loadMore,
    messages,
    sendMessage,
    streamingContent,
    thinkingContent,
    toolEvents,
    wsState,
  } = useChat({ threadId, authToken });

  // Assistant turn-end signal: fire once per isStreaming true→false
  // transition. The callback lives in a ref so consumers can pass inline
  // closures without re-triggering (or missing) a transition.
  const onAssistantTurnEndRef = useRef(onAssistantTurnEnd);
  onAssistantTurnEndRef.current = onAssistantTurnEnd;
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    if (isStreaming) {
      wasStreamingRef.current = true;
      return;
    }
    if (wasStreamingRef.current) {
      wasStreamingRef.current = false;
      onAssistantTurnEndRef.current?.();
    }
  }, [isStreaming]);

  // Lift wsState to parent for unified status display. This MUST run in an
  // effect, not the render body: onWsStateChange is ThreadView's setWsState,
  // and calling a parent's state setter during this component's render is
  // illegal (React "Cannot update a component while rendering a different
  // component"; frontend-doctrine render-purity rule). onWsStateChange is a
  // stable useCallback in ThreadView, so React's dep-compare gates the fire —
  // no prev-value ref needed.
  useEffect(() => {
    onWsStateChange?.(wsState);
  }, [wsState, onWsStateChange]);

  // Auto-wake on open (stage-1 idle-pod reaper, web path). Local web chat opens
  // the WS directly to the pod, so when the pod is reaped the input is disabled
  // and there's no Convex touchpoint on connect — "wake on typing" is impossible.
  // Instead, opening the chat while scaled_down resumes the pod via startInstance
  // (which no-ops server-side unless scaled_down). The WS then reconnects and
  // flushes any queued message. NOTE: with an aggressive idle threshold this
  // oscillates while the tab is open+idle — accepted by design for local testing.
  // idle-pod-reaper-local-brief.md §wake.
  const startInstance = useMutation(api.podControls.startInstance);
  const wakeFiredRef = useRef(false);
  useEffect(() => {
    if (podState === "scaled_down" && !wakeFiredRef.current) {
      wakeFiredRef.current = true;
      void startInstance().catch(() => {});
    } else if (podState === "running") {
      wakeFiredRef.current = false;
    }
  }, [podState, startInstance]);

  // ── Composer soft-lock while the agent opens the conversation ──
  //
  // The kickoff (claw §17.4) makes the agent speak FIRST. Between opening the
  // thread and that opener landing there is a real window in which the composer
  // is live and empty, and a user who types into it gets a crossed
  // conversation: their message and the scripted opener race, and BOOTSTRAP.md
  // shapes the reply to the FIRST INBOUND, which is now theirs.
  //
  // Anything the agent has already said clears the lock — a persisted assistant
  // row, a stream in flight, or streamed text on screen. `isLoading` is
  // excluded so the lock never engages on an unresolved transcript: a loading
  // state has no assistant message YET, which is not the same fact as none.
  const hasAgentSpoken =
    messages.some((m) => m.role === "assistant") ||
    isStreaming ||
    streamingContent.length > 0;
  const [kickoffWaitExpired, setKickoffWaitExpired] = useState(false);
  const kickoffPending =
    Boolean(awaitingKickoff) && !isLoading && !hasAgentSpoken && !kickoffWaitExpired;

  useEffect(() => {
    if (!kickoffPending) return;
    const timer = setTimeout(
      () => setKickoffWaitExpired(true),
      KICKOFF_WAIT_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [kickoffPending]);

  const dropDisabled = wsState !== "connected" || isAgentActive;
  const dragCounter = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);
  // Starter-template prefill: a nonce lets re-picking the same template
  // re-trigger the composer sync in ChatInput. Lazily seeded from an optional
  // one-shot `initialDraft` (e.g. the workflows "Describe a new job" seed) so
  // the composer is prefilled once on mount; the user reviews + sends manually.
  const [draft, setDraft] = useState<{ text: string; nonce: number } | null>(() =>
    initialDraft ? { text: initialDraft, nonce: Date.now() } : null,
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (dropDisabled) return;
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
      dragCounter.current += 1;
      if (dragCounter.current === 1) setIsDragOver(true);
    },
    [dropDisabled],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (dropDisabled) return;
      e.preventDefault();
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) setIsDragOver(false);
    },
    [dropDisabled],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (dropDisabled) return;
      if (!e.dataTransfer.types.includes("Files")) return;
      e.preventDefault();
    },
    [dropDisabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (dropDisabled) return;
      e.preventDefault();
      dragCounter.current = 0;
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length) attachments.addFiles(files);
    },
    [attachments, dropDisabled],
  );

  if (isPodError) {
    return (
      <div className={cn("flex flex-col items-center justify-center gap-3 p-8", className)}>
        <p className="text-sm font-bold text-brand-coral">Your agent encountered an error.</p>
        {podStateDetail && (
          <p className="max-w-md text-center text-xs text-black/50 dark:text-white/50">{podStateDetail}</p>
        )}
        <p className="text-xs text-black/40 dark:text-white/40">Check the dashboard for details.</p>
      </div>
    );
  }

  return (
    <div
      className={cn("relative flex min-h-0 flex-col", className)}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="shrink-0">
        <PodStatusBanner isProvisioning={isProvisioning} podState={podState} podStateDetail={podStateDetail} wsState={wsState} />
      </div>
      {isLoading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-brand-coral border-t-transparent" />
        </div>
      ) : (
        <MessageList
          isConnected={wsState === "connected"}
          messages={messages}
          streamingContent={streamingContent}
          thinkingContent={thinkingContent}
          toolEvents={toolEvents}
          isStreaming={isStreaming}
          isWaitingForResponse={isWaitingForResponse}
          onPickTemplate={(text) => setDraft({ text, nonce: Date.now() })}
          starterSet={starterSet}
          canLoadMore={canLoadMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
          onOpenNote={onOpenNote}
        />
      )}
      <div className="shrink-0">
        {kickoffPending && (
          <p className="px-5 pb-1 text-xs text-black/45 dark:text-white/45">
            Clawbert is starting the conversation…
          </p>
        )}
        <ChatInput
          attachments={attachments}
          creditStatus={credit.status}
          disabled={dropDisabled || kickoffPending}
          onSend={sendMessage}
          draft={draft}
        />
      </div>
      {isDragOver && (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-brand-coral bg-white/70 backdrop-blur-sm dark:bg-black/60">
          <span className="text-sm font-bold uppercase tracking-wider text-brand-coral-dark dark:text-brand-coral">
            Drop file to attach
          </span>
        </div>
      )}
    </div>
  );
}
